import React, { useEffect, useRef, useState } from 'react';
import { getBirthdayVideoForDate, getTodayDate } from './birthdayConfig';
import useFullscreen from '../hooks/useFullscreen';
import useIframeWatchdog from '../hooks/useIframeWatchdog';

function getYouTubeVideoId(videoSource) {
  if (typeof videoSource !== 'string') {
    return null;
  }

  try {
    const parsedUrl = new URL(videoSource, window.location.origin);
    const host = parsedUrl.hostname.replace(/^www\./, '');

    if (host === 'youtu.be') {
      return parsedUrl.pathname.replace('/', '') || null;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsedUrl.pathname === '/watch') {
        return parsedUrl.searchParams.get('v') || null;
      }

      const embedMatch = parsedUrl.pathname.match(/^\/embed\/([^/?#]+)/);
      if (embedMatch) {
        return embedMatch[1];
      }
    }
  } catch (error) {
    return null;
  }

  return null;
}

function Birthday() {
  const pageRef = useRef(null);
  const videoRef = useRef(null);
  const { isFullscreen, toggleFullscreen } = useFullscreen(pageRef);
  const today = getTodayDate();
  const videoSrc = getBirthdayVideoForDate(today);
  const youtubeVideoId = getYouTubeVideoId(videoSrc);
  const isYouTubeVideo = Boolean(youtubeVideoId);
  const youtubeEmbedSrc = youtubeVideoId
    ? `https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=1&loop=1&playlist=${youtubeVideoId}&controls=0&modestbranding=1&rel=0&playsinline=1`
    : null;
  const { iframeSrc } = useIframeWatchdog(youtubeEmbedSrc);
  const [isLoading, setIsLoading] = useState(Boolean(videoSrc));
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    setVideoError(false);
    setIsLoading(Boolean(videoSrc));
  }, [videoSrc]);

  return (
    <div
      ref={pageRef}
      className="dxb-video-page"
      onDoubleClick={toggleFullscreen}
    >
      {videoSrc ? (
        isYouTubeVideo ? (
          <iframe
            className="dxb-youtube-frame"
            src={iframeSrc}
            title="Birthday video"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            onLoad={() => setIsLoading(false)}
          />
        ) : (
          <video
            ref={videoRef}
            className="dxb-video-frame"
            src={videoSrc}
            autoPlay
            muted
            loop
            controls
            playsInline
            preload="auto"
            onLoadedData={() => setIsLoading(false)}
            onPlaying={() => setIsLoading(false)}
            onWaiting={() => setIsLoading(true)}
            onError={() => {
              setVideoError(true);
              setIsLoading(false);
            }}
            onEnded={(event) => {
              const video = event.currentTarget;
              video.currentTime = 0;
              video.play().catch(() => { });
            }}
          />
        )
      ) : (
        <div className="no-birthday">No birthday today</div>
      )}
      {videoSrc && isLoading && !videoError && (
        <div className="video-status-message">Loading birthday video...</div>
      )}
      {videoSrc && videoError && (
        <div className="video-status-message">

        </div>
      )}
      <button
        type="button"
        className="fullscreen-toggle-button"
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? 'EX' : 'FS'}
      </button>

      <style>{`
        .dxb-video-page {
          position: fixed;
          inset: 0;
          background: #000;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .dxb-video-frame {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .dxb-youtube-frame {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100vw;
          height: 56.25vw;
          min-width: 177.78vh;
          min-height: 100vh;
          transform: translate(-50%, -50%);
          border: 0;
          pointer-events: none;
        }

        .no-birthday {
          color: white;
          font-size: 24px;
        }

        .video-status-message {
          position: absolute;
          bottom: 16px;
          left: 16px;
          z-index: 4;
          color: #fff;
          background: rgba(0, 0, 0, 0.55);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 6px;
          padding: 8px 10px;
          font-size: 13px;
        }

        .fullscreen-toggle-button {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 3;
          width: 34px;
          height: 34px;
          border: 1px solid rgba(255, 255, 255, 0.75);
          border-radius: 6px;
          background: rgba(0, 0, 0, 0.45);
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
          cursor: pointer;
          padding: 0;
        }
      `}</style>
    </div>
  );
}

export default Birthday;
